<!-- eslint-disable vue/no-unused-refs -->
<template>
    <pendo-popover
        v-if="!isAutoProvisionedTrialExpired"
        ref="popoverContent"
        :open="isOpen"
        trigger="manual"
        placement="bottom-end"
        :popover-class="['notifications__popover', 'top-nav__popover']"
        @update:open="handlePopoverUpdate">
        <button
            v-pendo-tooltip="{
                content: $t('notifications.Notifications'),
                arrow: true,
            }"
            data-cy="notifications-dropdown-trigger"
            pendo-analytics="notifications-nav-item"
            aria-haspopup
            class="top-nav__trigger top-nav__button-trigger"
            :class="{
                'is-open': isOpen,
            }"
            :aria-label="$t('notifications.Notifications')"
            :aria-expanded="isOpen.toString()"
            :any-unread="anyUnread"
            @click="handleClick"
            @keydown.tab="handleTabKeydown"
            @keydown.space.prevent="handlePopoverUpdate(!isOpen)"
            @keyup.enter.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.left.prevent="$emit('left')"
            @keydown.right.prevent="$emit('right')">
            <notifications-icon :any-unread="anyUnread" />
        </button>
        <template #popover>
            <div
                ref="notificationsContent"
                class="notifications__popover-content"
                data-cy="notifications-dropdown-content"
                @keydown.escape.prevent="handleEscapePopover">
                <pendo-loading-indicator v-if="isLoading" />
                <template v-else>
                    <div class="notifications__header">
                        <h2 class="nav-title">
                            {{ $t('notifications.Notifications') }}
                        </h2>
                        <pendo-button
                            data-cy="mark-all-as-read-button"
                            icon="check"
                            size="small"
                            type="tertiary"
                            class="notifications__mark-as-read"
                            :label="$t('notifications.Mark all as read')"
                            :disabled="!anyUnread"
                            @click="markNotificationsAsRead" />
                    </div>
                    <div class="notifications__list">
                        <template v-if="formattedNotifications.length === 0">
                            <div
                                class="notifications__empty-state"
                                data-cy="notifications-empty-state">
                                <pendo-empty-state
                                    :title="$t('notifications.No notifications yet')"
                                    :description="
                                        $t(
                                            'notifications.We\'ll let you know when someone engages with your comments or mentions you.',
                                        )
                                    ">
                                    <template #icon>
                                        <img
                                            class="notifications__empty-state-image"
                                            src="@img/notification-empty-state.svg">
                                    </template>
                                </pendo-empty-state>
                            </div>
                        </template>
                        <template v-else>
                            <notification-list
                                :notifications="last7DaysNotifications"
                                :label="$t('notifications.Last 7 days')"
                                @card-clicked="onCardClicked"
                                @manage-access="onManageAccess" />
                            <notification-list
                                :notifications="olderThan7DaysNotifications"
                                :label="$t('notifications.Older than 7 days')"
                                @card-clicked="onCardClicked"
                                @manage-access="onManageAccess" />
                        </template>
                    </div>
                </template>
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import { PendoTooltip, PendoPopover, PendoButton, PendoEmptyState, PendoLoadingIndicator } from '@pendo/components';
import NotificationsIcon from '@/top-nav/components/icons/notifications-icon.vue';
import { getVeltClient } from '@/collaboration/collaboration.utils';
import {
    formatTimestamp,
    groupByDateRange,
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from '@/collaboration/notifications/notifications.utils';
import NotificationList from '@/collaboration/notifications/notification-list.vue';
import { DRAWERS } from '@/voc/constants/overlays.js';
import { useDrawers } from '@/voc/use/use-drawers.js';
import { mapGetters, mapActions } from 'vuex';
import { getFirstFocusableNode } from '@/utils/dom.utils.js';

export default {
    name: 'NotificationsDropdown',
    components: {
        PendoPopover,
        PendoButton,
        NotificationsIcon,
        NotificationList,
        PendoEmptyState,
        PendoLoadingIndicator,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open', 'close', 'select', 'focusTrigger', 'left', 'right'],
    data () {
        return {
            notifications: [],
            notificationSubscription: null,
            isLoading: true,
        };
    },
    computed: {
        ...mapGetters({
            dashboardById: 'dashboards/dashboardById',
            hasCollaborationAccess: 'collaboration/hasCollaborationAccess',
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
        }),
        formattedNotifications () {
            return this.notifications.map((notification) => ({
                ...notification,
                formattedTimestamp: notification.formattedTimestamp || formatTimestamp(notification.timestamp),
                isUnread: notification.isUnread === undefined ? true : notification.isUnread,
            }));
        },
        last7DaysNotifications () {
            return groupByDateRange(this.formattedNotifications).last7Days;
        },
        olderThan7DaysNotifications () {
            return groupByDateRange(this.formattedNotifications).olderThan7Days;
        },
        anyUnread () {
            return this.notifications.some((notification) => notification.isUnread);
        },
    },
    watch: {
        hasCollaborationAccess: {
            handler (newVal) {
                if (newVal) {
                    this.fetchNotifications();
                }
            },
            immediate: true,
        },
        'formattedNotifications.length': {
            handler (newVal) {
                if (newVal > 0 && this.isOpen) {
                    this.$nextTick(() => {
                        this.focusFirstOption();
                    });
                }
            },
            immediate: true,
        },
    },
    beforeUnmount () {
        this.notificationSubscription?.unsubscribe();
    },
    methods: {
        ...mapActions({
            loadOneDashboard: 'dashboards/loadOne',
        }),
        handleClick (event) {
            if (event.detail === 0) {
                return;
            }

            this.handlePopoverUpdate(!this.isOpen);
        },
        handlePopoverUpdate (open) {
            if (open) {
                this.$emit('open');
                this.focusFirstOption();
            } else {
                this.$emit('close');
            }
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstFocusableNode = getFirstFocusableNode(this.$refs.notificationsContent);
                if (firstFocusableNode) {
                    firstFocusableNode.focus();
                }
            }, 50);
        },
        async fetchNotifications () {
            this.isLoading = true;
            const client = await getVeltClient();
            const notificationElement = client.getNotificationElement();

            this.notificationSubscription = notificationElement
                ?.getNotificationsData()
                .subscribe((allNotifications) => {
                    const notifications = allNotifications?.filter((notification) => notification.forYou);

                    if (Array.isArray(notifications)) {
                        this.notifications = notifications;
                        this.isLoading = false;
                    }
                });
        },
        async onCardClicked (notificationId) {
            const notification = this.formattedNotifications.find((notif) => notif.id === notificationId);
            if (!notification) {
                return;
            }

            await markNotificationAsRead(notificationId);

            this.$emit('select');

            if (notification.notificationSource === 'comment') {
                await this.handleCommentNotification(notification);
            } else {
                await this.handleRequestNotification(notification);
            }
        },
        async handleCommentNotification (notification) {
            const { targetElementId, pageInfo, context, metadata } = notification.notificationSourceData;
            const path = notification.notificationSourceData.pageInfo.path.replace(/\/s\/\d+/, ''); // strip /s/:subId from path

            const query = { commentId: notification.notificationSourceData.annotationId };

            const pathSegments = path.split('/');
            if (pathSegments[1] === 'dashboards') {
                query.widgetId = targetElementId;
            } else if (pathSegments[1] === 'listen') {
                const { openItem } = Object.fromEntries([...new URLSearchParams(pageInfo.commentUrl.split('?')[1])]);
                query.openItem = openItem ?? metadata?.clientDocumentId?.split('/')[2] ?? '';
            }

            if (context?.customUrl) {
                const { pathname, searchParams } = new URL(context.customUrl);
                const queryParams = Object.fromEntries(searchParams);
                const href = this.$router.pendo.toPath({ path: pathname, query: queryParams });
                this.$router.push(href);
            } else {
                const href = this.$router.pendo.toPath({ path, query });
                this.$router.push(href);
            }

            if (pathSegments[1] === 'listen') {
                if (query.openItem) {
                    const { openDrawer } = useDrawers('voc');

                    openDrawer({
                        drawer: DRAWERS.vocItem,
                        metadata: {
                            vocItemId: query.openItem,
                            type: pathSegments[2] === 'validate' ? 'idea' : 'feedback',
                        },
                    });
                }
            }
        },
        async handleRequestNotification (notification) {
            if (!notification || !notification.displayHeadlineMessageTemplateData) {
                return;
            }
            const { requestEntityId } = notification.displayHeadlineMessageTemplateData;
            const hasDashboard = this.dashboardById(requestEntityId);

            let canAccess = hasDashboard;

            if (!canAccess) {
                canAccess = await this.checkDashboardAccess(requestEntityId);
            }

            if (canAccess) {
                const dashboardPath = this.$router.pendo.toPath(`/dashboards/${requestEntityId}`);
                this.$router.push(dashboardPath);
            }
        },
        async checkDashboardAccess (dashboardId) {
            try {
                await this.loadOneDashboard({ id: dashboardId, reFetch: true });

                return !!this.dashboardById(dashboardId);
            } catch {
                return false;
            }
        },
        async onManageAccess (data) {
            const { entityType, entityId, notificationId } = data;
            await markNotificationAsRead(notificationId);

            if (entityType === 'Dashboard') {
                const href = this.$router.pendo.toPath({
                    name: 'Dashboard',
                    params: { id: entityId },
                    query: { showShareModal: true },
                });
                this.$router.push(href);
            }

            this.$emit('select');
        },
        async markNotificationsAsRead () {
            await markAllNotificationsAsRead();
        },
        handleEscapePopover () {
            this.$emit('close');
            this.$emit('focusTrigger');
        },
        handleTabKeydown ($event) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                this.handlePopoverUpdate(!this.isOpen);
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

:deep(.top-nav__button-trigger) {
    .filled-icon,
    .empty-icon {
        transition: none;
    }
}

:deep(.pendo-loading-parent--relative) {
    height: 300px;
    width: 350px;

    .pendo-loading {
        height: 100%;
    }
}

.nav-title {
    padding: 0;
    margin: 0;
}

.notifications {
    &__popover-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__header {
        padding: 12px 24px 12px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid var(--palette-gray-30);
    }

    &__mark-as-read {
        color: var(--palette-gray-90);
        font-size: 13px;
        height: 20px;
        line-height: 20px;
    }

    &__list {
        flex-grow: 1;
        padding: 0 12px 12px 12px;
    }

    &__empty-state {
        padding: 40px;
        display: flex;
        justify-content: center;

        :deep(.pendo-empty-state) {
            position: unset;
            width: 300px;
            height: unset;
        }

        :deep(.pendo-empty-state__inner) {
            max-width: unset;
        }

        :deep(.pendo-empty-state__title) {
            font-size: 14px;
            line-height: 150%;
            color: var(--palette-gray-90);
        }

        :deep(.pendo-empty-state__description) {
            font-size: 13px;
            line-height: 150%;
            color: var(--palette-gray-70);
        }
    }
}
</style>

<style lang="less">
.top-nav__popover {
    .notifications__popover {
        width: 350px;

        .notifications__popover-content:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -1px;
            border-radius: 4px;
        }
    }
}
</style>
